/**
 * Smart Guides Hook
 * Provides Figma/Illustrator-style alignment guides during object dragging
 * - Shows red guide lines when objects align (center, edges)
 * - Auto-snaps to alignment points within threshold
 */

import * as React from 'react'
import { dia } from '@joint/core'

const SNAP_THRESHOLD = 5 // Snap when within 5px of alignment
const GUIDE_COLOR = '#EF4444' // Red guide lines
const GUIDE_OPACITY = 0.8

interface AlignmentGuide {
  type: 'vertical' | 'horizontal'
  position: number // x for vertical, y for horizontal
  label?: string // Optional label (e.g., "Center", "Left")
  start: number // Start coordinate (y for vertical, x for horizontal)
  end: number // End coordinate (y for vertical, x for horizontal)
}

export function useSmartGuides(
  paper: dia.Paper | null,
  graph: dia.Graph | null
) {
  const selectedElementRef = React.useRef<dia.Element | null>(null)
  const guideLinesRef = React.useRef<SVGLineElement[]>([])
  const draggingElementRef = React.useRef<dia.Element | null>(null)
  const isCtrlKeyPressed = React.useRef(false)

  React.useEffect(() => {
    if (!paper || !graph) return

    // Avoid unnecessary re-creation of refs
    selectedElementRef.current = null
    draggingElementRef.current = null
    guideLinesRef.current = []
    isCtrlKeyPressed.current = false

    const paperSvg = paper.svg as SVGSVGElement
    if (!paperSvg) return

    // Find the viewport layer (where elements are rendered)
    const viewport = paperSvg.querySelector('.joint-viewport') as SVGGElement

    // Create guide lines container
    const guidesGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    guidesGroup.id = 'smart-guides'
    guidesGroup.style.pointerEvents = 'none'

    // Add to viewport if exists, otherwise add to paper root
    if (viewport) {
      viewport.appendChild(guidesGroup)
    } else {
      paperSvg.appendChild(guidesGroup)
    }

    // Clear existing guide lines
    const clearGuides = () => {
      guideLinesRef.current.forEach(line => line.remove())
      guideLinesRef.current = []
    }

    // Create guide line element
    const createGuideLine = (guide: AlignmentGuide): SVGLineElement => {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')

      // Draw line only between the aligned objects
      if (guide.type === 'vertical') {
        line.setAttribute('x1', String(guide.position))
        line.setAttribute('y1', String(guide.start))
        line.setAttribute('x2', String(guide.position))
        line.setAttribute('y2', String(guide.end))
      } else {
        line.setAttribute('x1', String(guide.start))
        line.setAttribute('y1', String(guide.position))
        line.setAttribute('x2', String(guide.end))
        line.setAttribute('y2', String(guide.position))
      }

      line.setAttribute('stroke', GUIDE_COLOR)
      line.setAttribute('stroke-width', '1')
      line.setAttribute('stroke-dasharray', '5,5')
      line.setAttribute('opacity', String(GUIDE_OPACITY))
      line.setAttribute('pointer-events', 'none')
      line.style.zIndex = '9999'

      return line
    }

    // Find alignment guides for selected element
    const findAlignmentGuides = (selectedElement: dia.Element): AlignmentGuide[] => {
      const guides: AlignmentGuide[] = []
      const selectedBBox = selectedElement.getBBox()
      const selectedCenterX = selectedBBox.x + selectedBBox.width / 2
      const selectedCenterY = selectedBBox.y + selectedBBox.height / 2

      // Get all other elements (exclude selected element and non-data elements)
      const selectableElements = graph.getElements().filter(el => {
        const elementData = el.get('data') as any
        return el.id !== selectedElement.id && elementData?.type
      })

      selectableElements.forEach(targetElement => {
        const targetBBox = targetElement.getBBox()
        const targetCenterX = targetBBox.x + targetBBox.width / 2
        const targetCenterY = targetBBox.y + targetBBox.height / 2

        // Calculate range for guide lines (between selected and target objects)
        const verticalStart = Math.min(selectedBBox.y, targetBBox.y)
        const verticalEnd = Math.max(selectedBBox.y + selectedBBox.height, targetBBox.y + targetBBox.height)
        const horizontalStart = Math.min(selectedBBox.x, targetBBox.x)
        const horizontalEnd = Math.max(selectedBBox.x + selectedBBox.width, targetBBox.x + targetBBox.width)

        // Check vertical alignments (X axis)
        // Left edge alignment
        if (Math.abs(selectedBBox.x - targetBBox.x) < SNAP_THRESHOLD) {
          guides.push({
            type: 'vertical',
            position: targetBBox.x,
            label: 'Left',
            start: verticalStart,
            end: verticalEnd
          })
        }

        // Center-X alignment
        if (Math.abs(selectedCenterX - targetCenterX) < SNAP_THRESHOLD) {
          guides.push({
            type: 'vertical',
            position: targetCenterX,
            label: 'CenterX',
            start: verticalStart,
            end: verticalEnd
          })
        }

        // Right edge alignment
        if (Math.abs(selectedBBox.corner().x - targetBBox.corner().x) < SNAP_THRESHOLD) {
          guides.push({
            type: 'vertical',
            position: targetBBox.corner().x,
            label: 'Right',
            start: verticalStart,
            end: verticalEnd
          })
        }

        // Check horizontal alignments (Y axis)
        // Top edge alignment
        if (Math.abs(selectedBBox.y - targetBBox.y) < SNAP_THRESHOLD) {
          guides.push({
            type: 'horizontal',
            position: targetBBox.y,
            label: 'Top',
            start: horizontalStart,
            end: horizontalEnd
          })
        }

        // Center-Y alignment
        if (Math.abs(selectedCenterY - targetCenterY) < SNAP_THRESHOLD) {
          guides.push({
            type: 'horizontal',
            position: targetCenterY,
            label: 'CenterY',
            start: horizontalStart,
            end: horizontalEnd
          })
        }

        // Bottom edge alignment
        if (Math.abs(selectedBBox.corner().y - targetBBox.corner().y) < SNAP_THRESHOLD) {
          guides.push({
            type: 'horizontal',
            position: targetBBox.corner().y,
            label: 'Bottom',
            start: horizontalStart,
            end: horizontalEnd
          })
        }
      })

      // Merge guides at the same position (instead of discarding duplicates)
      // This ensures guide lines extend through ALL aligned objects
      const mergedMap = new Map<string, AlignmentGuide>()
      guides.forEach(guide => {
        const key = `${guide.type}_${Math.round(guide.position)}`
        const existing = mergedMap.get(key)
        if (existing) {
          existing.start = Math.min(existing.start, guide.start)
          existing.end = Math.max(existing.end, guide.end)
        } else {
          mergedMap.set(key, { ...guide })
        }
      })

      return Array.from(mergedMap.values())
    }

    // Apply snap to element position (closest snap wins when multiple guides conflict)
    const applySnap = (element: dia.Element, guides: AlignmentGuide[]) => {
      if (guides.length === 0) return

      const currentPos = element.position()
      const bbox = element.getBBox()
      let bestSnapX = currentPos.x
      let bestSnapY = currentPos.y
      let bestDeltaX = Infinity
      let bestDeltaY = Infinity

      guides.forEach(guide => {
        if (guide.type === 'vertical') {
          let candidateX = currentPos.x
          if (guide.label === 'Left') {
            candidateX = guide.position
          } else if (guide.label === 'CenterX') {
            candidateX = guide.position - bbox.width / 2
          } else if (guide.label === 'Right') {
            candidateX = guide.position - bbox.width
          }
          const delta = Math.abs(candidateX - currentPos.x)
          if (delta < bestDeltaX) {
            bestDeltaX = delta
            bestSnapX = candidateX
          }
        } else {
          let candidateY = currentPos.y
          if (guide.label === 'Top') {
            candidateY = guide.position
          } else if (guide.label === 'CenterY') {
            candidateY = guide.position - bbox.height / 2
          } else if (guide.label === 'Bottom') {
            candidateY = guide.position - bbox.height
          }
          const delta = Math.abs(candidateY - currentPos.y)
          if (delta < bestDeltaY) {
            bestDeltaY = delta
            bestSnapY = candidateY
          }
        }
      })

      // Apply snapped position
      if (bestSnapX !== currentPos.x || bestSnapY !== currentPos.y) {
        element.position(bestSnapX, bestSnapY, { skipGuides: true })
      }
    }

    // Show guides during element dragging
    const showGuides = (guides: AlignmentGuide[]) => {
      clearGuides()

      guides.forEach(guide => {
        const line = createGuideLine(guide)
        guidesGroup.appendChild(line)
        guideLinesRef.current.push(line)
      })

      if (guides.length > 0) {
      }
    }

    // Handle element drag start
    const handleDragStart = (elementView: dia.ElementView) => {
      draggingElementRef.current = elementView.model as dia.Element
    }

    // Handle element drag (position change) — throttled to animation frame
    let dragRafId: number | null = null
    const handleDrag = (element: dia.Element, _newPosition: dia.Point, opt: any) => {
      if (opt?.skipGuides) return
      if (draggingElementRef.current?.id !== element.id) return

      if (dragRafId !== null) return // Skip if already queued
      dragRafId = requestAnimationFrame(() => {
        dragRafId = null
        const guides = findAlignmentGuides(element)
        showGuides(guides)
        applySnap(element, guides)
      })
    }

    // Handle element drag end
    const handleDragEnd = () => {
      clearGuides()
      draggingElementRef.current = null
    }

     // Add global keydown/keyup event listeners for Ctrl key
     const handleKeyDown = (event: KeyboardEvent) => {
       if (event.ctrlKey) {
         isCtrlKeyPressed.current = true
         // If an element is currently selected, show guides when Ctrl is pressed
         if (selectedElementRef.current) {
           const guides = findAlignmentGuides(selectedElementRef.current)
           showGuides(guides)
         }
       }
     }

     const handleKeyUp = (event: KeyboardEvent) => {
       if (!event.ctrlKey) {
         isCtrlKeyPressed.current = false
         clearGuides()
       }
     }

     // Attach global key event listeners
     window.addEventListener('keydown', handleKeyDown)
     window.addEventListener('keyup', handleKeyUp)

     // Attach event listeners for selection and dragging
     const handleElementSelect = (elementView: dia.ElementView) => {
       const element = elementView.model as dia.Element
       selectedElementRef.current = element

       // If Ctrl is pressed, show alignment guides
       if (isCtrlKeyPressed.current) {
         const guides = findAlignmentGuides(element)
         showGuides(guides)
       }
     }

     const handleBlankSelect = () => {
       // Clear selected element and guides when clicking on blank space
       selectedElementRef.current = null
       // Only clear guides if Ctrl is not pressed
       if (!isCtrlKeyPressed.current) {
         clearGuides()
       }
     }

     paper.on('element:pointerdown', handleElementSelect)
     paper.on('blank:pointerdown', handleBlankSelect)
     paper.on('element:pointerdown', handleDragStart)
     graph.on('change:position', handleDrag)
     paper.on('element:pointerup', handleDragEnd)

      // Cleanup: remove global key event listeners
      return () => {
        window.removeEventListener('keydown', handleKeyDown)
        window.removeEventListener('keyup', handleKeyUp)
        
        if (paper) {
          paper.off('element:pointerdown', handleElementSelect)
          paper.off('blank:pointerdown', handleBlankSelect)
          paper.off('element:pointerdown', handleDragStart)
          paper.off('element:pointerup', handleDragEnd)
        }
        
        if (graph) {
          graph.off('change:position', handleDrag)
        }
        
        clearGuides()
        guidesGroup.remove()
        if (dragRafId !== null) cancelAnimationFrame(dragRafId)
      }
  }, [paper, graph])
}
